import classNames from "classnames"
import useTranslation from "next-translate/useTranslation"
import Link from "next/link"
import { useState } from "react"
import SVG from "react-inlinesvg"
import { Tooltip } from "reactstrap"

import Icon, { IconName } from "@components/common/Icon"
import { selfAssessmentKey, workflowBoxProgressLabels } from "@services/selfAssessmentHelper"

import TranslatedHtml from "./TranslatedHtml"



interface IProps {
  active?: boolean
  complete?: boolean
  /** id for testing:
   * Because the "data-testid" is not able to be tied onto the ProgressBox-component itself,
   * it is passed through to the inside of the ProgressBox where it is tied to
   * the <a>-tag for clickable ProgressBox and to the <div>-tag for unclickable ProgressBox.
   */
  "data-testid"?: string
  linkTo?: string | { pathname: string; query: any }
  icon: IconName | IconName[]
  id?: string
  /**
   * Box will be displayed as a collection as boxes if true.
   *
   * @type boolean
   * @default false
   */
  isCardCollection?: boolean
  progress?: number
  showCheck?: boolean
  subtitle?: string
  title: string
  // simple tooltip text or html translation input (@see component: TranslatedHtml)
  tooltipText?: string | {
    content: string
    params?: Record<string, any>
  }
  /** styles for the inner content of the tooltip */
  tooltipClassname?: string
}

/**
 * this component shows a single workflow-step-card
 * it is used for workflow-components like ProjectWorkflow, FeedbackInvitationWorkflow, ChallengeWorkflow
 */
const ProgressBox: React.FC<IProps> = (props: IProps) => {
  const { t } = useTranslation("common")
  const {
    active = true,
    complete = false,
    linkTo,
    icon,
    progress,
    title,
    subtitle,
    showCheck = true,
    id,
    tooltipText,
    tooltipClassname = "",
    isCardCollection = false
  } = props

  const translatedTitle = t(title)
  const translatedSubtitle = t(subtitle)

  // state and state-function for the tooltip
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const toggleTooltip = () => {
    setTooltipOpen(!tooltipOpen)
  }


  /**
   *
   * @param complete is the box to be completed or not
   * @returns icon with specific classname, dependend of complete-state
   */
  const statusIcon = (completed: boolean) => completed
    ? <div aria-label="complete" className="workflow__box__body__status-icon text-success" data-testid={props["data-testid"] + "-complete-icon"}>
      <Icon name="check" size={28} />
    </div>
    : <div aria-label="incomplete" className="workflow__box__body__status-icon text-gray" data-testid={props["data-testid"] + "-complete-icon"}>
      <Icon name="check" size={28} />
    </div>


  // builds className depending on if the box is active or not
  const boxClassName = [
    "workflow__box",
    active ? "" : " inactive",
    isCardCollection ? "workflow__box--collection" : ""
  ].filter(Boolean).join(" ")

  /**
   * provides a background for a box, created from a set of svgs
   *
   * @param isCollection true, if it should look like a collection of boxes
   * @returns a div, looking like a box
   */
  const boxBackground = (isCollection = false) => (
    <div className={classNames("workflow__box__background", isCollection ? "workflow__box__background--collection" : "")}>
      {/* left border of first box */}
      <SVG src={"/assets/img/workflow-box/box-start-first.svg"} className={"box-start-first"} />

      {/* left border for second till last box */}
      <SVG src={"/assets/img/workflow-box/box-start-nth.svg"} className={"box-start-nth"} />
      <div></div>

      {/* right border from first till next-to-last box */}
      <SVG src={"/assets/img/workflow-box/box-end-nth.svg"} className={"box-end-nth"} />

      {/* right border for the last box*/}
      <SVG src={"/assets/img/workflow-box/box-end-last.svg"} className={"box-end-last"} />
    </div>
  )

  /**
   * provides the content of a progressBox, using the boxBackground
   */
  const progressBoxBody = (
    <>
      {isCardCollection && (
        boxBackground(true)
      )}
      {boxBackground()}
      <div className="workflow__box__body">
        <div className="workflow__box__body__iconarea">
          {Array.isArray(icon)
            ? icon.map(iconName => {
              return <Icon key={iconName} name={iconName} size={34} />
            })
            : <Icon name={icon} size={34} />}
        </div>
        <div className="workflow__box__body__text">
          {subtitle && <span className="workflow__box__body__text__project-milestone-prefix">{translatedSubtitle}</span>}
          <div className={"workflow__box__body__text__title " + (translatedTitle.length > 6 ? " workflow__box__body__text__title--smaller" : "")}
          >{translatedTitle}</div>
          { // have to check for "progress !== undefined" instead of simply "progress" b/c "0" is a valid value
            progress !== undefined && <span className="workflow__box__body__text__status">{t(selfAssessmentKey(progress, workflowBoxProgressLabels))}</span>}
        </div>
        {showCheck && statusIcon(complete)}
      </div>
    </>)

  return <>
    {active && linkTo
      ? <Link href={linkTo} passHref>
        <a aria-label="progress" className={boxClassName} id={id} data-testid={props["data-testid"]}>
          {progressBoxBody}
        </a>
      </Link>
      : <div className={boxClassName} id={id} data-testid={props["data-testid"]}>{progressBoxBody}</div>
    }
    {id && tooltipText &&
      <Tooltip target={id} toggle={toggleTooltip} isOpen={tooltipOpen} innerClassName={tooltipClassname}>
        {typeof tooltipText === "string"
          ? tooltipText
          : <TranslatedHtml content={tooltipText.content} params={tooltipText.params} />}
      </Tooltip>
    }
  </>
}

export default ProgressBox
